import { createPromise } from "@workspace/mobile-core";
import { reportTelemetryWarning } from "../../telemetry/telemetry";

export interface ReactNativeWebViewBridge {
  postMessage: (data: string) => void;
}

interface WebKitMessageHandler {
  postMessage: (data: string) => void;
}

interface WebKitNamespace {
  messageHandlers?: {
    ReactNativeWebView?: WebKitMessageHandler;
  };
}

declare global {
  interface Window {
    ReactNativeWebView?: ReactNativeWebViewBridge;
  }
}

export interface NativeRequest {
  actionType: string;
  promiseId?: string;
  [key: string]: unknown;
}

let hasReportedFailure = false;

const reportBridgeFailure = (
  actionType: string,
  reason: "bridge_missing" | "handler_threw",
  error?: unknown
): void => {
  // Report once per page load to avoid flooding telemetry when the
  // WebView has been torn down and every queued call fails.
  if (hasReportedFailure) return;
  hasReportedFailure = true;
  reportTelemetryWarning("Native bridge unavailable", {
    actionType,
    reason,
    error: error instanceof Error ? error.message : String(error ?? "")
  });
};

const getNativePostMessage = (): ((data: string) => void) | undefined => {
  const webkit = window.webkit as WebKitNamespace | undefined;
  const iosHandler = webkit?.messageHandlers?.ReactNativeWebView;
  if (typeof iosHandler?.postMessage === "function") {
    // Must stay a method call — the WebKit handler needs `this`.
    return (data) => iosHandler.postMessage(data);
  }

  const androidBridge = window.ReactNativeWebView;
  if (typeof androidBridge?.postMessage === "function") {
    return (data) => androidBridge.postMessage(data);
  }

  return undefined;
};

export const postToNative = (request: NativeRequest): boolean => {
  const postMessage = getNativePostMessage();
  if (!postMessage) {
    reportBridgeFailure(request.actionType, "bridge_missing");
    return false;
  }

  try {
    postMessage(JSON.stringify(request));
    return true;
  } catch (error) {
    reportBridgeFailure(request.actionType, "handler_threw", error);
    return false;
  }
};

export const requestFromNative = async <T>(
  actionType: string
): Promise<T | undefined> => {
  const { promiseId, promise } = createPromise();

  if (!postToNative({ actionType, promiseId })) {
    // Settle and free the pending entry so callers fall through to their defaults.
    window.CoreMobileApi?.resolvePromise(promiseId, undefined);
    return undefined;
  }

  const response: unknown = await promise;
  return response as T | undefined;
};
